/**
 * 全局状态（Store）
 * ------------------------------------------------------------
 * 只放「跨页面共享、数量很少」的状态：
 *   - 当前学生
 *   - 当前选中的教材/单元（"正在学哪一课"）
 *   - 用户设置
 * 其它数据一律从 services / engines 现查现用，避免状态重复。
 */
import { getStorage } from './storage/index.js';
import { COLLECTIONS } from './schema.js';
import { makeStudent } from './models.js';
import { uid, nowISO } from '../utils/index.js';
import { emit, EVENTS } from './events.js';

const DEFAULT_SETTINGS = {
  speakRate: 0.9,
  autoSpeak: true,        // 单词题是否自动发音
  showPhonetic: true,     // 是否展示音标
  soundEffect: true,      // 答对/答错音效
  dailyGoal: 30,          // 每日目标题数
  allowSkip: false,       // 是否允许跳过题目
};

export const state = {
  ready: false,
  student: null,
  selection: { textbookId: '', unitId: '' },  // 当前学习位置
  settings: { ...DEFAULT_SETTINGS },
  textbooks: [],
};

/* --------------------------------- 设置 --------------------------------- */

export async function loadSettings() {
  const storage = getStorage();
  const row = await storage.get(COLLECTIONS.META, 'settings');
  state.settings = { ...DEFAULT_SETTINGS, ...(row?.value || {}) };
  return state.settings;
}

export async function saveSettings(patch) {
  state.settings = { ...state.settings, ...patch };
  const storage = getStorage();
  await storage.put(COLLECTIONS.META, { key: 'settings', value: state.settings });
  emit(EVENTS.SETTINGS_CHANGED, state.settings);
  return state.settings;
}

/* --------------------------------- 学生 --------------------------------- */

/** 取当前学生；没有就创建一个默认学生（首次打开即用） */
export async function ensureStudent() {
  const storage = getStorage();
  const meta = await storage.get(COLLECTIONS.META, 'currentStudentId');
  const all = await storage.list(COLLECTIONS.STUDENTS);
  let student = all.find(s => s.id === meta?.value) || all[0];
  if (!student) {
    student = makeStudent({ id: uid('stu'), name: '小小学习者', avatar: '🦊' });
    await storage.put(COLLECTIONS.STUDENTS, student);
    await storage.put(COLLECTIONS.META, { key: 'currentStudentId', value: student.id });
  }
  state.student = student;
  return student;
}

export async function listStudents() {
  const storage = getStorage();
  return storage.list(COLLECTIONS.STUDENTS);
}

export async function switchStudent(id) {
  const storage = getStorage();
  const student = await storage.get(COLLECTIONS.STUDENTS, id);
  if (!student) throw new Error('学生不存在');
  await storage.put(COLLECTIONS.META, { key: 'currentStudentId', value: id });
  state.student = student;
  emit(EVENTS.STUDENT_CHANGED, student);
  emit(EVENTS.PROGRESS_CHANGED);
  return student;
}

export async function createStudent({ name, avatar = '🐻' }) {
  const storage = getStorage();
  const student = makeStudent({ id: uid('stu'), name: name || '新同学', avatar });
  await storage.put(COLLECTIONS.STUDENTS, student);
  return switchStudent(student.id);
}

/** 更新学生字段（改名、换头像、加 XP） */
export async function updateStudent(patch) {
  if (!state.student) return null;
  const next = { ...state.student, ...patch, updatedAt: nowISO() };
  await getStorage().put(COLLECTIONS.STUDENTS, next);
  state.student = next;
  emit(EVENTS.STUDENT_CHANGED, next);
  return next;
}

/** XP → 等级：每 100 XP 一级，返回 { level, xpInLevel, nextAt, progress } */
export function levelFromXp(xp) {
  const total = Math.max(0, Number(xp) || 0);
  const level = Math.floor(total / 100) + 1;
  const xpInLevel = total % 100;
  return { level, xp: total, xpInLevel, nextAt: 100, progress: xpInLevel / 100 };
}

/* ------------------------------ 当前学习位置 ------------------------------ */

export async function loadSelection() {
  const storage = getStorage();
  const row = await storage.get(COLLECTIONS.META, 'selection');
  if (row?.value) state.selection = { ...state.selection, ...row.value };
  return state.selection;
}

export async function saveSelection(sel) {
  state.selection = { ...state.selection, ...sel };
  await getStorage().put(COLLECTIONS.META, { key: 'selection', value: state.selection });
  return state.selection;
}

/** 一次性完成初始化（main.js 调用） */
export async function bootstrap() {
  await loadSettings();
  await ensureStudent();
  await loadSelection();
  state.ready = true;
  return state;
}
